import * as tslib_1 from "tslib";
import { combineLatest, ReplaySubject, BehaviorSubject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { Selectors, Actions } from 'store/root-reducer';
import { RoleType } from 'modules/shared/models/role.models';
import { OrderType, ArtemisOrder } from '@artemis-blocks/order';
var UserNotificationDataSource = /** @class */ (function () {
    function UserNotificationDataSource(store, takeDefaultUsers, currentDomain) {
        this.store = store;
        this.takeDefaultUsers = takeDefaultUsers;
        this.currentDomain = currentDomain;
        this.orderItems = [
            { value: 'checked', type: OrderType.DESC }
        ];
        this.unsubscribe$ = new ReplaySubject(1);
        // Keep all users in this domain
        this.fullUserNotificationList = [];
        // Keep all users for current page, source of data
        this.currentPageData = new BehaviorSubject([]);
        // Keep selected users as array.
        this.selectedUsers = [];
    }
    UserNotificationDataSource.prototype.connect = function (collectionViewer) {
        var _this = this;
        this.getUserDataAndProcess();
        this.loadUserData(this.payload);
        this.paginator.page
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(function () { return _this.loadCurrentPage(); });
        return this.currentPageData;
    };
    UserNotificationDataSource.prototype.disconnect = function (collectionViewer) {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    };
    UserNotificationDataSource.prototype.setCurrentDomain = function (domain) {
        this.currentDomain = domain;
        this.selectedUsers = [];
    };
    UserNotificationDataSource.prototype.getUserDataAndProcess = function () {
        var _this = this;
        combineLatest([
            this.store.select(Selectors.customer.getUsers),
            this.store.select(Selectors.customer.getCustomer),
            this.store.select(Selectors.customer.getDefaultNotifiedUsers),
        ])
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(function (_a) {
            var accounts = _a[0], customer = _a[1], currentDomainDefaultUserList = _a[2];
            _this.paginator.length = accounts.count > accounts.results.length ? accounts.count : accounts.results.length;
            // get all users that has at least L1 or L3 or L3 in current domain
            if (accounts.results && _this.currentDomain) {
                // We filter on the backend already by domain
                var filteredAccounts = accounts.results.filter(function (account) {
                    return account.roles.some(function (role) { return role.domain === _this.currentDomain.id; });
                });
                _this.fullUserNotificationList = filteredAccounts.map(function (account) {
                    return {
                        checked: false,
                        name: account.name,
                        email: account.email,
                        l1: account.roles.some(function (role) { return (role.name === RoleType.L1 && role.domain === _this.currentDomain.id); }),
                        l2: account.roles.some(function (role) { return (role.name === RoleType.L2 && role.domain === _this.currentDomain.id); }),
                        l3: account.roles.some(function (role) { return (role.name === RoleType.L3 && role.domain === _this.currentDomain.id); }),
                        oauthId: account.oauthId,
                    };
                });
                // if in customer access control page, update notify checkbox according to `users` field of current customer
                if (customer && customer.users && _this.fullUserNotificationList.length) {
                    var customersList = customer.users.map(function (userOauthId) {
                        return _this.fullUserNotificationList.find(function (user) {
                            return user.oauthId === userOauthId;
                        });
                    }).filter(function (item) { return item; });
                    _this.updateSelectedUserList(customersList);
                }
            }
            // set defualt notified users as checked and save to this.selectedUsers
            if (_this.takeDefaultUsers && currentDomainDefaultUserList && currentDomainDefaultUserList.length > 0) {
                _this.updateSelectedUserList(currentDomainDefaultUserList);
            }
            // when user is in this.selectedUsers but is not checked yet
            // eg. when current user is added to selected user in onboarding page by parent component
            if (_this.selectedUsers.length > 0) {
                _this.setCheckboxStatusInTable(_this.selectedUsers.map(function (user) { return user.oauthId; }));
            }
            // sort data and put selected users on top
            _this.fullUserNotificationList = ArtemisOrder.order(_this.fullUserNotificationList, _this.orderItems).slice();
            _this.loadCurrentPage();
        });
    };
    /**
     * @method setCheckboxStatusInTable
     * Pass in a list of user oauth id and set them as selected
     */
    UserNotificationDataSource.prototype.setCheckboxStatusInTable = function (usersToBeChecked) {
        var _this = this;
        usersToBeChecked.forEach(function (userOauthId) {
            var matchedIndex = _this.fullUserNotificationList.findIndex(function (role) { return role.oauthId === userOauthId; });
            if (!!~matchedIndex) {
                _this.fullUserNotificationList[matchedIndex].checked = true;
            }
        });
    };
    /**
     * @method setCheckboxStatusInTable
     * Pass in a list of user oauth id and set them as selected
     */
    UserNotificationDataSource.prototype.updateSelectedUserList = function (usersToBeAdded) {
        var _this = this;
        if (usersToBeAdded && usersToBeAdded.length) {
            if (this.selectedUsers && this.selectedUsers.length) {
                usersToBeAdded.forEach(function (user) {
                    var matchedUser = _this.selectedUsers.find(function (selectedUser) { return selectedUser.oauthId === user.oauthId; });
                    if (!matchedUser) {
                        _this.selectedUsers.push(user);
                    }
                });
            }
            else {
                this.selectedUsers = usersToBeAdded.slice();
            }
            this.setCheckboxStatusInTable(this.selectedUsers.map(function (user) { return user.oauthId; }));
        }
    };
    /**
     * @method loadCurrentPage
     * Load current page users only
     */
    UserNotificationDataSource.prototype.loadCurrentPage = function () {
        this.currentPageData.next(this.fullUserNotificationList.slice(this.paginator.pageSize * this.paginator.pageIndex, this.paginator.pageSize * this.paginator.pageIndex + this.paginator.pageSize));
    };
    /**
     * @method selectCurrentUser
     * If there no default users, parent component will call this method to select current user
     */
    UserNotificationDataSource.prototype.selectCurrentUser = function (currentUserOauthId) {
        var _this = this;
        if (!this.selectedUsers.some(function (user) { return user.oauthId === currentUserOauthId; })) {
            combineLatest([
                this.store.select(Selectors.auth.getAuth).pipe(take(1)),
                this.store.select(Selectors.auth.getUserRoles)
            ])
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe(function (_a) {
                var currentProfile = _a[0], currentRoles = _a[1];
                var currentUser = {
                    checked: true,
                    name: currentProfile.user.name || 'Current Profile',
                    email: currentProfile.user.email,
                    l1: currentRoles.some(function (role) { return role.name === RoleType.L1; }),
                    l2: currentRoles.some(function (role) { return role.name === RoleType.L2; }),
                    l3: currentRoles.some(function (role) { return role.name === RoleType.L3; }),
                    oauthId: currentUserOauthId
                };
                _this.updateSelectedUserList([currentUser]);
            });
        }
    };
    /**
     * @method loadUserData
     * Dispatch GetUsers to load user list from BE
     */
    UserNotificationDataSource.prototype.loadUserData = function (payload) {
        this.payload = Object.assign({}, payload);
        if (this.payload && this.payload.domainObject && this.payload.domainObject.id !== this.currentDomain.id) {
            this.currentDomain = payload.domainObject;
            this.selectedUsers = [];
        }
        if (!this.paginator.pageSize) {
            this.paginator.pageSize = this.paginator.pageSizeOptions[0];
        }
        // get all users and apply sort and pagination later at frontend
        var userRequest = tslib_1.__assign({ limit: 999, offset: 0 }, this.payload);
        this.store.dispatch(new Actions.customer.GetUsers(userRequest));
    };
    UserNotificationDataSource.prototype.selectItem = function (selectedUser, checked) {
        selectedUser.checked = checked;
        if (checked) {
            this.selectedUsers.push(selectedUser);
        }
        else {
            this.selectedUsers = this.selectedUsers.filter(function (selected) { return selected.oauthId !== selectedUser.oauthId; });
        }
    };
    UserNotificationDataSource.prototype.getSelectedUsers = function () {
        return this.selectedUsers;
    };
    /**
     * @method clearAllSelected
     * Set all users to unselected
     */
    UserNotificationDataSource.prototype.clearAllSelected = function () {
        var _this = this;
        this.fullUserNotificationList.forEach(function (user) {
            user.checked = false;
            _this.selectedUsers = [];
        });
    };
    return UserNotificationDataSource;
}());
export { UserNotificationDataSource };
